import { useState } from 'react'
import { Button, EmptyState, LoadingState } from '@orbind/ui'
import { ErroDaConversa } from './estados.tsx'
import {
  BUSCA_MINIMA_DE_PESSOAS,
  TOGGLE_CONTATOS_EXTERNOS,
  type ConviteDeContato,
  type PessoaDoOrbind,
} from '@orbind/core'
import { useFeatureToggle } from '../../lib/feature-toggle.ts'
import { ExplorerDrawer } from '../../pages/chat-shared.tsx'
import { usePresence } from '../presence/PresenceProvider.tsx'
import { avatarDoAutor, useAcoesDeContato, useBuscaDePessoas, useContatos } from './useChat.ts'

/**
 * Nova conversa — a gaveta do `+` da Comunicação, no molde do mensageiro de
 * referência: novo canal no topo, depois as pessoas.
 *
 * - Quem é do WORKSPACE aparece direto, sem convite: já são da empresa.
 * - Pessoas de OUTRAS empresas (busca, convite, aceite) só com o toggle
 *   CHAT_EXTERNAL_CONTACTS — em avaliação pelo dono do produto. Tudo que é de
 *   fora mora nas seções marcadas abaixo, para sair inteiro se ele disser não.
 *
 * Tudo acontece aqui dentro da Comunicação, inclusive aceitar convite.
 */
export function NovaConversa({
  telaInicial,
  onFechar,
  onNovoCanal,
  onAbrirConversa,
}: {
  telaInicial: 'inicio' | 'convites'
  onFechar: () => void
  onNovoCanal: () => void
  onAbrirConversa: (memberId: string, nome: string) => void
}) {
  const [tela, setTela] = useState(telaInicial)
  const [busca, setBusca] = useState('')
  const { eu, membros } = usePresence()

  const externosLigados = useFeatureToggle(TOGGLE_CONTATOS_EXTERNOS)
  const contatos = useContatos(externosLigados)
  const pessoasDeFora = useBuscaDePessoas(externosLigados, busca)
  const acoes = useAcoesDeContato()

  const termo = busca.trim().toLowerCase()
  const combina = (nome: string) => termo === '' || nome.toLowerCase().includes(termo)
  const daEmpresa = membros.filter((m) => m.id !== eu && m.isActive && combina(m.name))
  const meusContatos = (contatos.data?.contatos ?? []).filter((p) => combina(p.name))
  const convites = contatos.data?.convites ?? []
  const recebidos = convites.filter((c) => c.direcao === 'recebido')
  const enviados = convites.filter((c) => c.direcao === 'enviado')

  if (tela === 'convites') {
    return (
      <ExplorerDrawer title="Convites" subtitle="Pessoas de outras empresas" onClose={() => setTela('inicio')}>
        <div className="ch-contacts ch-contacts--lista">
          {contatos.isPending ? (
            <LoadingState rows={3} label="Carregando convites" />
          ) : contatos.isError ? (
            // rede fora dizia "Nenhum convite", e a pessoa concluía que
            // ninguém a convidou
            <div style={{ padding: '24px 10px' }}>
              <ErroDaConversa erro={contatos.error} oQue="os convites" onTentarDeNovo={() => contatos.refetch()} />
            </div>
          ) : convites.length === 0 ? (
            <div style={{ padding: '24px 10px' }}>
              <EmptyState
                icon="fas fa-envelope-open"
                title="Nenhum convite"
                description="Quando alguém de outra empresa quiser conversar com você, o convite aparece aqui."
              />
            </div>
          ) : (
            <>
              {recebidos.length > 0 ? <Rotulo texto="Recebidos" /> : null}
              {recebidos.map((c) => (
                <LinhaDeConvite key={c.id} convite={c}>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={acoes.aceitar.isPending && acoes.aceitar.variables === c.id}
                    // Aceitou, já cai na conversa — é o que a pessoa quer fazer em seguida.
                    onClick={() =>
                      acoes.aceitar.mutate(c.id, { onSuccess: () => onAbrirConversa(c.pessoa.memberId, c.pessoa.name) })
                    }
                  >
                    Aceitar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => acoes.recusar.mutate(c.id)}>
                    Recusar
                  </Button>
                </LinhaDeConvite>
              ))}
              {enviados.length > 0 ? <Rotulo texto="Enviados" /> : null}
              {enviados.map((c) => (
                <LinhaDeConvite key={c.id} convite={c}>
                  <Button size="sm" variant="ghost" onClick={() => acoes.cancelar.mutate(c.id)}>
                    Cancelar
                  </Button>
                </LinhaDeConvite>
              ))}
            </>
          )}
        </div>
      </ExplorerDrawer>
    )
  }

  return (
    <ExplorerDrawer title="Nova conversa" subtitle="Pessoas e canais" onClose={onFechar}>
      <div className="ch-grouppick">
        <div className="ch-groupsearch">
          <i className="fas fa-search" aria-hidden />
          <input
            type="search"
            autoFocus
            value={busca}
            placeholder={externosLigados ? 'Pesquisar nome na empresa ou no Orbind' : 'Pesquisar nome'}
            aria-label="Pesquisar pessoas"
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <div className="ch-contacts ch-contacts--lista">
        <button type="button" className="ch-contact" onClick={onNovoCanal}>
          <span className="ch-contact__avatar">
            <i className="fas fa-users" aria-hidden />
          </span>
          <span className="ch-contact__main">
            <span className="ch-contact__name">Novo canal</span>
            <span className="ch-contact__sub">Grupo para a equipe</span>
          </span>
        </button>

        {/* ---- de fora da empresa (toggle CHAT_EXTERNAL_CONTACTS) ---- */}
        {externosLigados ? (
          <button type="button" className="ch-contact" onClick={() => setTela('convites')}>
            <span className="ch-contact__avatar">
              <i className="fas fa-envelope" aria-hidden />
            </span>
            <span className="ch-contact__main">
              <span className="ch-contact__name">Convites</span>
              <span className="ch-contact__sub">
                {recebidos.length} {recebidos.length === 1 ? 'recebido' : 'recebidos'} · {enviados.length}{' '}
                {enviados.length === 1 ? 'enviado' : 'enviados'}
              </span>
            </span>
            {recebidos.length > 0 ? (
              <span className="ch-row__unread" aria-label={`${recebidos.length} convites recebidos`}>
                {recebidos.length}
              </span>
            ) : null}
          </button>
        ) : null}

        <Rotulo texto="Da empresa" />
        {daEmpresa.length === 0 ? (
          <span className="ch-contact__sub ch-contacts__vazio">
            {termo === '' ? 'Ninguém mais neste workspace.' : 'Ninguém da empresa com esse nome.'}
          </span>
        ) : (
          daEmpresa.map((m) => (
            <LinhaDePessoa
              key={m.id}
              pessoa={{ memberId: m.id, name: m.name, avatarUrl: m.avatarUrl, hasAvatar: m.hasAvatar, avatarVersion: m.avatarVersion }}
              sub={m.email}
              onClick={() => onAbrirConversa(m.id, m.name)}
            />
          ))
        )}

        {/* ---- de fora da empresa (toggle CHAT_EXTERNAL_CONTACTS) ---- */}
        {externosLigados && meusContatos.length > 0 ? (
          <>
            <Rotulo texto="Contatos de outras empresas" />
            {meusContatos.map((p) => (
              <LinhaDePessoa key={p.memberId} pessoa={p} sub="Contato" onClick={() => onAbrirConversa(p.memberId, p.name)} />
            ))}
          </>
        ) : null}

        {externosLigados ? (
          <>
            <Rotulo texto="Outras pessoas do Orbind" />
            {termo.length < BUSCA_MINIMA_DE_PESSOAS ? (
              <span className="ch-contact__sub ch-contacts__vazio">
                Digite pelo menos {BUSCA_MINIMA_DE_PESSOAS} letras para buscar pessoas de outras empresas.
              </span>
            ) : pessoasDeFora.isPending ? (
              <LoadingState rows={2} label="Buscando pessoas" />
            ) : (pessoasDeFora.data ?? []).filter((p) => p.situacao !== 'contato').length === 0 ? (
              <span className="ch-contact__sub ch-contacts__vazio">Ninguém de fora encontrado com esse nome.</span>
            ) : (
              (pessoasDeFora.data ?? [])
                // Contato aceito já aparece na seção de contatos acima.
                .filter((p) => p.situacao !== 'contato')
                .map((p) => (
                  <LinhaDePessoa key={p.memberId} pessoa={p} sub="Outra empresa">
                    <AcaoDaPessoa pessoa={p} acoes={acoes} />
                  </LinhaDePessoa>
                ))
            )}
          </>
        ) : null}
      </div>
    </ExplorerDrawer>
  )
}

function AcaoDaPessoa({ pessoa, acoes }: { pessoa: PessoaDoOrbind; acoes: ReturnType<typeof useAcoesDeContato> }) {
  if (pessoa.situacao === 'convite_enviado') {
    return (
      <Button size="sm" variant="ghost" icon="fas fa-check" disabled title="Aguardando a pessoa aceitar">
        Convite enviado
      </Button>
    )
  }
  if (pessoa.situacao === 'convite_recebido' && pessoa.conviteId) {
    const id = pessoa.conviteId
    return (
      <Button size="sm" variant="primary" onClick={() => acoes.aceitar.mutate(id)}>
        Aceitar
      </Button>
    )
  }
  return (
    <Button
      size="sm"
      variant="secondary"
      icon="fas fa-user-plus"
      loading={acoes.convidar.isPending && acoes.convidar.variables === pessoa.memberId}
      onClick={() => acoes.convidar.mutate(pessoa.memberId)}
    >
      Adicionar
    </Button>
  )
}

type PessoaVisivel = Pick<PessoaDoOrbind, 'memberId' | 'name' | 'avatarUrl' | 'hasAvatar' | 'avatarVersion'>

function LinhaDePessoa({
  pessoa,
  sub,
  onClick,
  children,
}: {
  pessoa: PessoaVisivel
  sub: string
  onClick?: () => void
  children?: React.ReactNode
}) {
  const foto = avatarDoAutor(pessoa)
  const conteudo = (
    <>
      <span className="ch-contact__avatar">
        {foto ? <img className="ch-avatar__img" src={foto} alt="" /> : <i className="fas fa-user" aria-hidden />}
      </span>
      <span className="ch-contact__main">
        <span className="ch-contact__name">{pessoa.name}</span>
        <span className="ch-contact__sub">{sub}</span>
      </span>
      {children}
    </>
  )
  // Com ação à direita a linha não é botão: botão dentro de botão é HTML inválido.
  return onClick ? (
    <button type="button" className="ch-contact" onClick={onClick}>
      {conteudo}
    </button>
  ) : (
    <div className="ch-contact ch-contact--estatico">{conteudo}</div>
  )
}

function LinhaDeConvite({ convite, children }: { convite: ConviteDeContato; children: React.ReactNode }) {
  return (
    <LinhaDePessoa
      pessoa={convite.pessoa}
      sub={`${convite.direcao === 'recebido' ? 'Quer conversar com você' : 'Aguardando resposta'} · ${new Date(
        convite.enviadoEm,
      ).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`}
    >
      {children}
    </LinhaDePessoa>
  )
}

function Rotulo({ texto }: { texto: string }) {
  return <span className="ch-info__rotulo ch-contacts__rotulo">{texto}</span>
}
